import { createContext } from 'react';
import type { User, GetTokenSilentlyOptions, IdToken, LogoutOptions } from '@auth0/auth0-react';
// GetTokenSilentlyVerboseResponse is reconstructed locally in psp-contract
// (Omit<TokenEndpointResponse, 'refresh_token'>) because @auth0/auth0-react
// does not re-export the spa-js alias. Source it from the contract so the
// shape stays in lockstep with the bridge's overloaded signature.
import type { GetTokenSilentlyVerboseResponse } from '@opengov/psp-contract';

/** Options for `loginWithRedirect` and `logout`. Honored in standalone mode; ignored (with a warn) in shell mode. */
export type ShellRedirectOptions = Pick<NonNullable<LogoutOptions['logoutParams']>, 'returnTo'>;

/**
 * Curated subset of `@auth0/auth0-react`'s `Auth0ContextInterface`. Every
 * method behaves the same in standalone and shell mode, except:
 *
 *   - `loginWithRedirect` is a no-op + console.warn in shell mode.
 *   - `logout({ returnTo })` is honored in standalone mode; ignored (with a
 *     warn) in shell mode — returnTo is owned by the PSP shell.
 *   - `isLoading` is the raw `useAuth0().isLoading`, not a derived value.
 */

export interface ShellAuth0ContextInterface {
  isAuthenticated: boolean;
  /** Raw `useAuth0().isLoading` — Auth0 SDK semantics, not derived. */
  isLoading: boolean;
  user: User | undefined;
  error: Error | undefined;
  /** Overload mirrors @auth0/auth0-react: verbose response when detailedResponse is true. */
  getAccessTokenSilently: {
    (
      options: GetTokenSilentlyOptions & { detailedResponse: true }
    ): Promise<GetTokenSilentlyVerboseResponse>;
    (options?: GetTokenSilentlyOptions): Promise<string>;
  };
  getIdTokenClaims: () => Promise<IdToken | undefined>;
  loginWithRedirect: (options?: ShellRedirectOptions) => Promise<void>;
  logout: (options?: ShellRedirectOptions) => Promise<void>;
}

/** Internal context. Null default forces `useShellAuth0` to throw a clear
 *  error when used outside a `ShellAuth0Provider`. */
export const ShellAuth0Context = createContext<ShellAuth0ContextInterface | null>(null);
